import { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_solar-scada-monitor/artifacts/k1rohnzb_Logo.png";

const NAV_LABELS = {
  en: [
    { to: "/", label: "Home" },
    { to: "/products", label: "Products" },
    { to: "/about", label: "About" },
    { to: "/projects", label: "Projects" },
    { to: "/partners", label: "Partners" },
    { to: "/contact", label: "Contacts" },
  ],
  pt: [
    { to: "/", label: "Início" },
    { to: "/products", label: "Produtos" },
    { to: "/about", label: "Sobre" },
    { to: "/projects", label: "Projetos" },
    { to: "/partners", label: "Parceiros" },
    { to: "/contact", label: "Contactos" },
  ]
};

// ATUALIZADO: Adicionada a rota principal "/projects" no topo do submenu
const PROJECTS_SUBMENU = {
  en: [
    { to: "/projects", label: "Projects" },
    { to: "/clients", label: "Clients" },
    { to: "/sucessstories", label: "Success stories" },
  ],
  pt: [
    { to: "/projects", label: "Projetos" },
    { to: "/clients", label: "Clientes" },
    { to: "/sucessstories", label: "Histórias de sucesso" },
  ]
};

const FlagEN = () => (
  <svg className="h-3.5 w-5 flex-shrink-0 rounded-sm object-cover" viewBox="0 0 50 30" xmlns="http://www.w3.org/2000/svg">
    <clipPath id="t"><path d="M0 0h50v30H0z"/></clipPath>
    <g clipPath="url(#t)">
      <path d="M0 0h50v30H0z" fill="#012169"/>
      <path d="m0 0 50 30M50 0 0 30" stroke="#fff" strokeWidth="6"/>
      <path d="m0 0 50 30M50 0 0 30" stroke="#C8102E" strokeWidth="4"/>
      <path d="M19 0h12v30H19zm-19 9h50v12H0z" fill="#fff"/>
      <path d="M22 0h6v30H22zm-22 12h50v6H0z" fill="#C8102E"/>
    </g>
  </svg>
);

const FlagPT = () => (
  <svg className="h-3.5 w-5 flex-shrink-0 rounded-sm object-cover" viewBox="0 0 600 400" xmlns="http://www.w3.org/2000/svg">
    <rect width="240" height="400" fill="#006600"/>
    <rect x="240" width="360" height="400" fill="#ff0000"/>
    <g transform="translate(240, 200)">
      <circle r="80" fill="none" stroke="#ffff00" strokeWidth="6"/>
      <circle r="55" fill="none" stroke="#ffff00" strokeWidth="4"/>
      <path d="M-75 -25 L75 25 M-75 25 L75 -25 M-80 0 L80 0 M0 -80 L0 80" stroke="#ffff00" strokeWidth="4" fill="none"/>
      <path d="M-30 -45 h60 v40 c0 20 -15 35 -30 45 c-15 -10 -30 -25 -30 -45 z" fill="#ffffff" stroke="#000000" strokeWidth="1.5"/>
      <path d="M-30 -45 h60 v40 c0 20 -15 35 -30 45 c-15 -10 -30 -25 -30 -45 z" fill="none" stroke="#ff0000" strokeWidth="8"/>
      <path d="M-14 -25 h28 v22 c0 10 -7 16 -14 21 c-7 -5 -14 -11 -14 -21 z" fill="#000099"/>
      <rect x="-24" y="-41" width="8" height="8" fill="#ffff00"/>
      <rect x="16" y="-41" width="8" height="8" fill="#ffff00"/>
      <rect x="-4" y="-41" width="8" height="8" fill="#ffff00"/>
      <rect x="-26" y="-12" width="8" height="8" fill="#ffff00"/>
      <rect x="18" y="-12" width="8" height="8" fill="#ffff00"/>
    </g>
  </svg>
);

const LANGUAGES = [
  { code: "en", label: "English", flag: <FlagEN /> },
  { code: "pt", label: "Português", flag: <FlagPT /> }
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [forceHideProjects, setForceHideProjects] = useState(false);
  const [mobileProjectsOpen, setMobileProjectsOpen] = useState(false);
  
  const menuRef = useRef(null);
  const { language, changeLanguage } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setLangMenuOpen(false);
    setMobileProjectsOpen(false);
    setForceHideProjects(false);
  }, [location.pathname]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setLangMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectLanguage = (code) => {
    changeLanguage(code);
    setLangMenuOpen(false);
    setOpen(false);
  };

  const handleNavClick = (to) => {
    if (location.pathname === to) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const onContact = location.pathname === "/contact";
  const currentNav = NAV_LABELS[language];
  const currentLanguageData = LANGUAGES.find(l => l.code === language);

  const isAnyProjectSubmenuActive = PROJECTS_SUBMENU[language].some(
    (sub) => sub.to === location.pathname
  );

  return (
    <header
      data-testid="site-header"
      className={`fixed left-0 right-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/60 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 md:px-12 lg:px-16">
        
        {/* Logo */}
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="WSBP" className="h-10 w-auto sm:h-11" draggable={false} />
        </Link>

        {/* Links Desktop */}
        <nav className="hidden items-center gap-1 lg:flex mx-auto">
          {currentNav.map((item) => {
            const isProjects = item.to === "/projects";

            if (isProjects) {
              return (
                <div 
                  key={item.to} 
                  className="relative group"
                  onMouseLeave={() => setForceHideProjects(false)}
                >
                  <NavLink
                    to={item.to}
                    onClick={() => handleNavClick(item.to)}
                    className={({ isActive }) =>
                      `relative inline-flex rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                        isActive || isAnyProjectSubmenuActive ? "text-[#F68A31]" : "text-white/75 hover:text-white"
                      }`
                    }
                  >
                    {({ isActive }) => {
                      const isHighlight = isActive || isAnyProjectSubmenuActive;
                      return (
                        <>
                          <span className="relative z-10 flex items-center gap-1 whitespace-nowrap">
                            {item.label}
                            <ChevronDown 
                              size={14} 
                              className="transition-transform duration-300 group-hover:-rotate-180 opacity-70" 
                            />
                          </span>
                          {isHighlight && (
                            <motion.span
                              layoutId="nav-active"
                              className="absolute inset-0 rounded-full border border-[#F68A31]/40 bg-[#F68A31]/10"
                              transition={{ type: "spring", stiffness: 380, damping: 30 }}
                            />
                          )}
                        </>
                      );
                    }}
                  </NavLink>
                  
                  {/* Dropdown Projects */}
                  <div className={`absolute top-full left-0 pt-2 transition-all duration-300 ${
                    forceHideProjects 
                      ? "hidden"
                      : "opacity-0 invisible group-hover:opacity-100 group-hover:visible"
                  }`}>
                    <div className="w-48 rounded-xl border border-white/10 bg-black/90 p-2 shadow-2xl backdrop-blur-xl">
                      {PROJECTS_SUBMENU[language].map((sub) => {
                        const isCurrentSubPage = location.pathname === sub.to;
                        return (
                          <Link
                            key={sub.label}
                            to={sub.to}
                            onClick={(e) => {
                              handleNavClick(sub.to);
                              setForceHideProjects(true);
                              e.currentTarget.blur();
                            }}
                            className={`block rounded-lg px-4 py-2.5 text-sm transition-colors ${
                              isCurrentSubPage 
                                ? "text-[#F68A31] bg-white/10 font-medium" 
                                : "text-white/75 hover:bg-white/10 hover:text-[#F68A31]"
                            }`}
                          >
                            {sub.label}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => handleNavClick(item.to)}
                className={({ isActive }) =>
                  `relative rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    isActive ? "text-[#F68A31]" : "text-white/75 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="relative z-10 whitespace-nowrap">{item.label}</span>
                    {isActive && (
                      <motion.span
                        layoutId="nav-active"
                        className="absolute inset-0 rounded-full border border-[#F68A31]/40 bg-[#F68A31]/10"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Zona direita: Idioma e Contacto */}
        <div className="hidden lg:flex items-center gap-4">
          <div 
            className="relative" 
            ref={menuRef}
            onMouseEnter={() => setLangMenuOpen(true)}
            onMouseLeave={() => setLangMenuOpen(false)}
          >
            <button
              onClick={() => setLangMenuOpen(!langMenuOpen)}
              className="flex w-[92px] items-center justify-between rounded-full border border-white/15 bg-white/5 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-white transition-colors hover:bg-white/10"
            >
              <div className="flex items-center gap-1.5">
                {currentLanguageData?.flag}
                <span>{language}</span>
              </div>
              <motion.div animate={{ rotate: langMenuOpen ? 180 : 0 }} transition={{ duration: 0.2 }} className="flex items-center">
                <ChevronDown size={14} className="text-white/50" />
              </motion.div>
            </button>

            <AnimatePresence>
              {langMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute right-0 mt-2 w-44 rounded-xl border border-white/10 bg-black/90 p-1.5 shadow-2xl backdrop-blur-xl"
                >
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => selectLanguage(lang.code)}
                      className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                        language === lang.code ? "bg-[#F68A31]/10 text-[#F68A31]" : "text-white/88 hover:bg-white/5 hover:text-white"
                      }`}
                    >
                      {lang.flag}
                      <span className={language === lang.code ? "font-medium" : ""}>{lang.label}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {!onContact && (
            <Link to="/contact" className="btn-primary text-sm w-48 h-10 justify-center items-center">
              <span className="text-center whitespace-nowrap">{language === "en" ? "Get in touch" : "Contacte-nos"}</span>
              <ArrowUpRight size={16} strokeWidth={2.5} className="flex-shrink-0 ml-1.5" />
            </Link>
          )}
        </div>

        {/* Mobile toggles */}
        <div className="flex items-center gap-3 lg:hidden">
          <div className="relative flex h-11 w-20 items-center justify-between rounded-full border border-white/15 bg-white/5 px-2.5 text-white">
            <div className="flex items-center gap-1 pointer-events-none">
              {currentLanguageData?.flag}
              <select
                value={language}
                onChange={(e) => selectLanguage(e.target.value)}
                className="pointer-events-auto bg-transparent text-xs font-semibold uppercase tracking-wider text-white outline-none appearance-none cursor-pointer"
              >
                <option value="en" className="bg-black text-white">EN</option>
                <option value="pt" className="bg-black text-white">PT</option>
              </select>
            </div>
            <ChevronDown size={12} className="pointer-events-none text-white/50" />
          </div>

          <button
            onClick={() => setOpen((o) => !o)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="border-t border-white/10 bg-black/85 backdrop-blur-xl lg:hidden max-h-screen overflow-y-auto"
          >
            <div className="flex flex-col gap-1 px-6 py-6 pb-24">
              {currentNav.map((item) => {
                const isProjects = item.to === "/projects";

                if (isProjects) {
                  return (
                    <div key={item.to} className="flex flex-col">
                      <div className="flex items-center relative">
                        <NavLink
                          to={item.to}
                          onClick={() => handleNavClick(item.to)}
                          className={({ isActive }) =>
                            `flex-1 rounded-xl px-4 py-3 font-display text-lg ${
                              isActive || isAnyProjectSubmenuActive ? "bg-[#F68A31]/10 text-[#F68A31]" : "text-white/85"
                            }`
                          }
                        >
                          {item.label}
                        </NavLink>
                        <button
                          onClick={() => setMobileProjectsOpen(!mobileProjectsOpen)}
                          className="absolute right-2 p-2 text-white/50 hover:text-white"
                        >
                          <ChevronDown size={20} className={`transition-transform duration-300 ${mobileProjectsOpen ? "rotate-180" : ""}`} />
                        </button>
                      </div>

                      <AnimatePresence>
                        {mobileProjectsOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden flex flex-col gap-1 pl-4 mt-1"
                          >
                            {PROJECTS_SUBMENU[language].map((sub) => {
                              const isCurrentSubPage = location.pathname === sub.to;
                              return (
                                <Link
                                  key={sub.label}
                                  to={sub.to}
                                  onClick={() => handleNavClick(sub.to)}
                                  className={`rounded-xl px-4 py-2.5 font-display text-base transition-colors ${
                                    isCurrentSubPage ? "text-[#F68A31] font-medium bg-white/5" : "text-white/60 hover:text-[#F68A31]"
                                  }`}
                                >
                                  {sub.label}
                                </Link>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => handleNavClick(item.to)}
                    className={({ isActive }) =>
                      `rounded-xl px-4 py-3 font-display text-lg ${
                        isActive ? "bg-[#F68A31]/10 text-[#F68A31]" : "text-white/85"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                );
              })}
              
              {!onContact && (
                <Link to="/contact" className="btn-primary mt-4 justify-center">
                  {language === "en" ? "Get in touch" : "Entre em contacto"} <ArrowUpRight size={16} />
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}